import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LoyaltyTagSectionAPIPromiseClient } from "gen/witbooking/loyalty/tag/v1/loyalty_tag_api_grpc_web_pb";
import { useCallback, useMemo } from "react";
import { useGrpcClient } from "../../../../lib/grpc";
import { LoyaltyTagSection } from "../../domain/LoyaltyTagSection";
import { LoyaltyTagSectionGateway } from "../gateways/LoyaltyTagSectionGateway";

const STALE_TIME = 60 * 1000;

type UseSectionOptions = {
  loadSections?: boolean;
};

type UseSectionsWithoutSections = {
  getSections: () => Promise<LoyaltyTagSection[]>;
  saveSection: (section: LoyaltyTagSection) => Promise<void>;
  updateSection: (section: LoyaltyTagSection) => Promise<void>;
  deleteSection: (id: string) => Promise<void>;
  reorderSection: (id: string, after?: string) => Promise<void>;
};

type UseSectionWithSections = UseSectionsWithoutSections & {
  sections: LoyaltyTagSection[] | undefined;
  isLoading: boolean;
};

type UseSections = UseSectionsWithoutSections | UseSectionWithSections;

export const sectionKeys = {
  all: ["sections"] as const,
  getAll: (brandId: string) => [...sectionKeys.all, brandId] as const,
  get: (brandId: string, id: string) => [...sectionKeys.getAll(brandId), "detail", id] as const,
};

export const useSection = (brandId: string, id?: string) => {
  const grpcClient = useGrpcClient(LoyaltyTagSectionAPIPromiseClient);

  const loyaltyTagSectionGateway = useMemo(() => new LoyaltyTagSectionGateway(grpcClient), [
    grpcClient,
  ]);

  const { data: section, isLoading } = useQuery({
    queryKey: sectionKeys.get(brandId, id ?? ""),
    queryFn: () => loyaltyTagSectionGateway.get(brandId, id ?? ""),
    staleTime: STALE_TIME,
    enabled: !!id,
  });

  return { section, isLoading };
};

export function useSections(brandId: string): UseSectionsWithoutSections;
export function useSections(brandId: string, options: UseSectionOptions): UseSectionWithSections;
export function useSections(brandId: string, options?: UseSectionOptions): UseSections {
  const queryClient = useQueryClient();
  const grpcClient = useGrpcClient(LoyaltyTagSectionAPIPromiseClient);

  const loyaltyTagSectionGateway = useMemo(() => new LoyaltyTagSectionGateway(grpcClient), [
    grpcClient,
  ]);

  const { data: sections, isLoading } = useQuery({
    queryKey: sectionKeys.getAll(brandId),
    queryFn: () => loyaltyTagSectionGateway.getAll(brandId),
    staleTime: STALE_TIME,
    enabled: (!!brandId && options?.loadSections) ?? false,
  });

  const invalidateSections = useCallback(
    (queryKey: ReadonlyArray<string>) => queryClient.invalidateQueries({ queryKey }),
    [queryClient]
  );

  const getSections = useCallback(() => {
    return queryClient.fetchQuery({
      queryKey: sectionKeys.getAll(brandId),
      queryFn: () => loyaltyTagSectionGateway.getAll(brandId),
      staleTime: STALE_TIME,
    });
  }, [brandId, loyaltyTagSectionGateway, queryClient]);

  const saveSection = useCallback(
    async (section: LoyaltyTagSection) => {
      await loyaltyTagSectionGateway.save(brandId, section);
      await invalidateSections(sectionKeys.getAll(brandId));
    },
    [brandId, invalidateSections, loyaltyTagSectionGateway]
  );

  const updateSection = useCallback(
    async (section: LoyaltyTagSection) => {
      await loyaltyTagSectionGateway.update(brandId, section);
      await invalidateSections(sectionKeys.getAll(brandId));
    },
    [brandId, invalidateSections, loyaltyTagSectionGateway]
  );

  const deleteSection = useCallback(
    async (id: string) => {
      await loyaltyTagSectionGateway.delete(brandId, id);
      await invalidateSections(sectionKeys.getAll(brandId));
    },
    [brandId, invalidateSections, loyaltyTagSectionGateway]
  );

  const reorderSection = useCallback(
    async (id: string, after?: string) => {
      await loyaltyTagSectionGateway.reorder(brandId, id, after);
      await invalidateSections(sectionKeys.getAll(brandId));
    },
    [brandId, invalidateSections, loyaltyTagSectionGateway]
  );

  return {
    sections,
    isLoading,
    getSections,
    saveSection,
    updateSection,
    deleteSection,
    reorderSection,
  };
}
